import { useState } from "react";
import { Sparkles, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { post } from "@/lib/api";

export interface DocumentSuggestions {
  tags: string[];
  mainPoint: string;
  titleSuggestions: string[];
  truncated: boolean;
}

interface AISuggestionsProps {
  documentId: number;
  /** Fills the main-point field. Applying is always the user's explicit act. */
  onUseMainPoint: (value: string) => void;
  /** Current field value, so we can avoid offering what's already there. */
  currentMainPoint?: string;
}

/**
 * Reads the draft and offers a main point and tags.
 *
 * Nothing here applies itself. Suggestions are proposals the author accepts one
 * at a time — the model reports what it found in their writing, it does not
 * edit the sermon.
 */
export function AISuggestions({
  documentId,
  onUseMainPoint,
  currentMainPoint,
}: AISuggestionsProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<DocumentSuggestions | null>(null);
  const [appliedTags, setAppliedTags] = useState<Set<string>>(new Set());
  const [applyingTag, setApplyingTag] = useState<string | null>(null);

  const run = async () => {
    setLoading(true);
    try {
      const result = await post<DocumentSuggestions>(
        `/ai/documents/${documentId}/suggestions`,
        {},
      );
      setSuggestions(result);
      setAppliedTags(new Set());
      if (result.truncated) {
        toast({
          title: "Long document",
          description: "Only the opening section was analysed.",
        });
      }
    } catch (err) {
      const status = (err as { status?: number })?.status;
      toast({
        title:
          status === 503
            ? "AI is unavailable"
            : status === 429
              ? "Slow down a moment"
              : "Couldn't read the draft",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const applyTag = async (name: string) => {
    setApplyingTag(name);
    try {
      await post(`/documents/${documentId}/tags/apply`, { names: [name] });
      setAppliedTags((prev) => new Set(prev).add(name));
    } catch (err) {
      toast({
        title: "Couldn't add tag",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setApplyingTag(null);
    }
  };

  const mainPointDiffers =
    Boolean(suggestions?.mainPoint) &&
    suggestions?.mainPoint.trim() !== (currentMainPoint || "").trim();

  return (
    <div className="rounded-md border border-dashed p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4" />
          <span>Read the draft and suggest</span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void run()}
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
              Reading…
            </>
          ) : suggestions ? (
            "Again"
          ) : (
            "Suggest"
          )}
        </Button>
      </div>

      {suggestions && (
        <div className="space-y-3">
          {suggestions.mainPoint ? (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Main point</p>
              <p className="text-sm leading-relaxed">{suggestions.mainPoint}</p>
              {mainPointDiffers ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onUseMainPoint(suggestions.mainPoint)}
                >
                  Use this
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Matches what you have.
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              No clear main point yet — the draft hasn&apos;t committed to one.
            </p>
          )}

          {suggestions.tags.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.tags.map((tag) => {
                  const applied = appliedTags.has(tag);
                  return (
                    <Button
                      key={tag}
                      type="button"
                      variant={applied ? "secondary" : "outline"}
                      size="sm"
                      className="h-7 text-xs"
                      disabled={applied || applyingTag === tag}
                      onClick={() => void applyTag(tag)}
                    >
                      {applied && <Check className="h-3 w-3 mr-1" />}
                      {applyingTag === tag && (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      )}
                      {tag}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {suggestions.titleSuggestions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                Title ideas
              </p>
              <ul className="text-sm space-y-0.5">
                {suggestions.titleSuggestions.map((title) => (
                  <li key={title} className="text-muted-foreground">
                    {title}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
